import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { StepUpProvider } from "@/components/auth/StepUp";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { InstallBanner } from "@/components/InstallBanner";
import { DailyVerse } from "@/components/DailyVerse";
import { UnreadBadge, useUnreadCount } from "@/components/chat/UnreadBadge";
import { CompanySwitcher, CompanyChip } from "@/components/CompanySwitcher";
import { tmPages, canManageApplicants } from "@/lib/tmPages";
import {
  LayoutDashboard, Users, FileSearch, MessageSquare, Building2, LogOut, BadgeCheck, Receipt, ExternalLink, UserCog, ClipboardCheck, Truck, BookOpen,
  Link2, ShieldCheck, PenLine, Menu, Shuffle, ClipboardList,
} from "lucide-react";

const Item = ({ to, icon: Icon, label, testid, badge = 0, urgent = false }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      `side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 border-l-3 border-transparent ${isActive ? "active" : ""}`
    }
    data-testid={testid}
  >
    <Icon className="h-4 w-4" />
    <span>{label}</span>
    <UnreadBadge count={badge} testid={`${testid}-unread`} urgent={urgent} />
  </NavLink>
);

const Ext = ({ href, label, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid} className="side-link flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 border-l-3 border-transparent">
    <ExternalLink className="h-4 w-4" /><span>{label}</span>
  </a>
);

function Brand() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center overflow-hidden">
        <img src="/safer-highways-logo.png" alt="Safer Highways Inc" className="h-9 w-9 object-contain" data-testid="sidebar-logo" />
      </div>
      <div>
        <div className="tag-mono text-blue-300">SAFER HIGHWAYS INC</div>
        <div className="text-sm font-semibold">Consortium Portal</div>
      </div>
    </div>
  );
}

function SideNav({ user, isAdmin, onLogout, unread = 0, unstarted = 0, unstartedRush = 0, reportActivity = 0 }) {
  const isTm = user?.role === "terminal_manager";
  return (
    <div className="h-full flex flex-col bg-slate-950 text-slate-100">
      <div className="px-5 py-6 border-b border-slate-800"><Brand /></div>
      <CompanySwitcher user={user} />
      <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
        {isTm ? (
          <>
            <Item to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard-link" />
            {tmPages(user).includes("drivers") && <Item to="/drivers" icon={Users} label="Drivers" testid="nav-drivers-link" />}
            {tmPages(user).includes("certificates") && <Item to="/certificates" icon={BadgeCheck} label="Certificates" testid="nav-certificates-link" />}
            {tmPages(user).includes("fleet") && <Item to="/fleet" icon={Truck} label="Fleet & Filings" testid="nav-fleet-link" />}
            {tmPages(user).includes("randoms") && <Item to="/randoms" icon={Shuffle} label="Random Selections" testid="nav-randoms-link" />}
            {tmPages(user).includes("chat") && <Item to="/chat" icon={MessageSquare} label="Messages" testid="nav-chat-link" badge={unread} />}
            {tmPages(user).includes("applicants") && <Item to="/applicants" icon={ClipboardList} label="Applicants" testid="nav-applicants-link" />}
            <DailyVerse />
          </>
        ) : (
          <>
            <Item to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard-link" />
            <Item to="/drivers" icon={Users} label="Drivers" testid="nav-drivers-link" />
            <Item to="/certificates" icon={BadgeCheck} label="Certificates" testid="nav-certificates-link" />
            <Item to="/reports" icon={FileSearch} label="Report Requests" testid="nav-reports-link" badge={user?.role === "admin" ? unstarted : reportActivity} urgent={unstartedRush > 0} />
            <Item to="/chat" icon={MessageSquare} label="Messages" testid="nav-chat-link" badge={unread} />
            <Item to="/billing" icon={Receipt} label="Billing" testid="nav-billing-link" />
            <Item to="/consultant" icon={ClipboardCheck} label="Consultant" testid="nav-consultant-link" />
            <Item to="/fleet" icon={Truck} label="Fleet & Filings" testid="nav-fleet-link" />
            <Item to="/audits" icon={ShieldCheck} label="Compliance Audit" testid="nav-audits-link" />
            <Item to="/signatures" icon={PenLine} label="Signatures" testid="nav-signatures-link" />
            {canManageApplicants(user) && <Item to="/applicants" icon={ClipboardList} label="Applicants" testid="nav-applicants-link" />}
            <Item to="/randoms" icon={Shuffle} label="Random Selections" testid="nav-randoms-link" />
            {!isAdmin && <Item to="/company" icon={Building2} label="My Company" testid="nav-company-link" />}
            <Ext href="https://results.thesaferteam.com/login/" label="Randoms Management" testid="nav-randoms-management-link" />
            {!isAdmin && <Ext href="https://motus.dot.gov" label="FMCSA Portal (MOTUS)" testid="nav-fmcsa-motus-link" />}
            {isAdmin ? (
              <>
                <div className="px-5 pt-4 pb-1 tag-mono text-slate-500">Admin</div>
                <Item to="/customers" icon={Building2} label="Customers" testid="nav-customers-link" />
                <Item to="/links" icon={Link2} label="Quick Links" testid="nav-quick-links-link" />
                <DailyVerse />
              </>
            ) : <DailyVerse />}
          </>
        )}
      </nav>
      <div className="p-4 border-t border-slate-800">
        <div className="mb-1.5"><CompanyChip user={user} testid="sidebar-company-chip" /></div>
        <div className="text-xs text-slate-400 mb-1">{isTm && user?.terminal_name ? `Terminal · ${user.terminal_name}` : (user?.role === "admin" ? user?.company || "" : "")}</div>
        <NavLink to="/profile" className="block group" data-testid="profile-link">
          <div className="text-sm font-medium truncate group-hover:text-blue-300" data-testid="user-name-display">{user?.name}</div>
          <div className="text-xs text-slate-400 truncate">{user?.email}</div>
          <div className="text-xs text-blue-300 mt-1 flex items-center gap-1"><UserCog className="h-3 w-3" /> My profile</div>
        </NavLink>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/training-guide.pdf`} target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-1 text-xs text-slate-300 hover:text-white" data-testid="training-guide-link"><BookOpen className="h-3 w-3" /> Training guide (PDF)</a>
        <Button variant="ghost" size="sm" className="mt-3 w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800" onClick={onLogout} data-testid="logout-button">
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const unread = useUnreadCount(!!user);
  const [unstarted, unstartedRush] = useUnreadCount(user?.role === "admin", "/reports/unstarted-count", "count", "rush");
  const reportActivity = useUnreadCount(!!user && user.role !== "admin", "/reports/activity-count", "count");
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const doLogout = async () => { await logout(); nav("/login"); };
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) { setLastPath(pathname); setOpen(false); }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50">
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-3 px-3 py-2 bg-slate-950 text-slate-100 border-b border-slate-800" data-testid="mobile-topbar">
        <Button variant="ghost" size="icon" className="text-slate-100 hover:bg-slate-800 hover:text-white" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
        <div className="flex items-center gap-2 min-w-0">
          <img src="/safer-highways-logo.png" alt="" className="h-7 w-7 rounded bg-white object-contain p-0.5" />
          <div className="text-sm font-semibold truncate">Safer Highways</div>
          <CompanyChip user={user} className="max-w-[45vw]" testid="topbar-company-chip" />
        </div>
        <div className="w-10" />
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 border-slate-800 bg-slate-950 text-slate-100 [&>button]:text-slate-300" data-testid="mobile-nav-drawer">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SideNav user={user} isAdmin={isAdmin} onLogout={doLogout} unread={unread} unstarted={unstarted} unstartedRush={unstartedRush} reportActivity={reportActivity} />
        </SheetContent>
      </Sheet>
      <aside className="hidden lg:block w-64 shrink-0"><div className="sticky top-0 h-screen"><SideNav user={user} isAdmin={isAdmin} onLogout={doLogout} unread={unread} unstarted={unstarted} unstartedRush={unstartedRush} reportActivity={reportActivity} /></div></aside>
      <main className="flex-1 min-w-0 overflow-x-clip">
        <InstallBanner />
        <StepUpProvider><Outlet /></StepUpProvider>
      </main>
    </div>
  );
}
